import { Session } from '@customerio/hydra-authentication';
import {
  activeAccountIdStore,
  activeSessionIdStore,
  changeToSession,
  sessionsStore,
} from '@customerio/hydra-session-state';
import { getApiBaseUrl } from '@/services/api';

// Vitest sets `import.meta.env.MODE === 'test'`. Skipping `Session#initialize()`
// in tests avoids a module-load race against MSW: the singleton would otherwise
// fire `/accounts/current` before `server.listen()` runs, fail with a TypeError,
// and burn ~30s of retries before `isInitialized` flips — leaving `useAuthQuery`
// disabled long past any test's `waitFor` budget. Tests don't exercise the
// cross-app cache seed anyway (MSW handles the auth endpoints directly), so
// `isInitialized` is set to `true` below to mirror the production state that
// `useAuthQuery` cares about — that the seed step has finished one way or
// another.
const isTest = import.meta.env.MODE === 'test';

/**
 * Singleton `Session` for the Hydra React app.
 *
 * Constructed at module load so `Session#initialize()` kicks off as soon as
 * this file is imported. The initial `/accounts/current` fetch (or its
 * masq/workspace variants) seeds the cross-app cache that `servicesApi`
 * wrappers consult, so by the time `useAuthQuery` reads from
 * `servicesApi.getAccount*()` the response is normally served from cache
 * with no extra network round-trip.
 *
 * The instance is also exposed on `window.__hydra__.session` from
 * `utils/inject-globals.ts` for cross-app consumers (Journeys UI, Ember).
 */
export const session = new Session({
  baseUrl: `${getApiBaseUrl()}/v1`,
  activeSessionId: activeSessionIdStore,
  // Send the per-tab active-account selection on the bootstrap `/accounts/current`
  // fetch so `auth.account` reflects the switched-to account after a reload,
  // matching the account every data request already targets via `X-Active-Account-Id`.
  activeAccountId: activeAccountIdStore,
  // Multi-account login-loop fallback: if the persisted active slot is rejected
  // on boot (e.g. its account was logged out), `Session` retries the other known
  // slots and resolves to the first valid one via `changeToSession` (which
  // updates `activeSessionIdStore` and persists). See FPS-2206.
  sessions: sessionsStore,
  onActiveSessionIdResolved: changeToSession,
  initialize: !isTest,
});

if (isTest) {
  session.isInitialized.set(true);
}
